import { useRef } from 'react';

import {
    IBlockViewModel,
    IProductItemViewModel,
    IProductListCreativeItem,
} from 'Types/Litium';
import { ISrcSetSize } from 'Types/shared';
import oneBigSixSmall from 'Pages/ProductListingPage/ProductListUtils/oneBigSixSmall';
import sixSmallOneBig from 'Pages/ProductListingPage/ProductListUtils/sixSmallOneBig';
import BlockRenderer from 'Blocks/BlockRenderer';
import ProductCard from 'Components/ProductCard/ProductCard';

import ProductListCreativeItem from './ProductListCreativeItem';

const ProductList5GridBigImage = ({
    products,
    blockAreas,
    productListCreativeItems,
}: {
    products: IProductItemViewModel[];
    blockAreas: { [key: string]: IBlockViewModel[] };
    productListCreativeItems: IProductListCreativeItem[];
}) => {
    const gridRef = useRef<HTMLUListElement>(null);

    const productsLength = products.length;

    const isBigCard = (index: number) => {
        const bigLastOrFirst = index % 14 < 7;
        if (bigLastOrFirst) {
            return sixSmallOneBig.isCardBig(index, productsLength);
        }
        return oneBigSixSmall.isCardBig(index, productsLength);
    };

    const getSizeArray = (index: number): ISrcSetSize =>
        isBigCard(index)
            ? {
                  default: { width: 156, height: 222 },
                  md: { width: 356, height: 507 },
                  lg: { width: 353, height: 692 },
                  xl: { width: 520, height: 906 },
                  xxl: { width: 756, height: 1220 },
              }
            : {
                  default: { width: 156, height: 222 },
                  md: { width: 356, height: 507 },
                  lg: { width: 166, height: 238 },
                  xl: { width: 250, height: 357 },
                  xxl: { width: 368, height: 525 },
              };

    return (
        <ul
            className="grid grid-cols-2 gap-x-5 gap-y-3 lg:grid-cols-5 supports-[grid-template-rows:_subgrid]:lg:grid-rows-[auto]"
            ref={gridRef}
        >
            {products.map((product, index) => (
                <>
                    {index === 8 && blockAreas?.third?.length > 0 && (
                        <li
                            key="block-area-third"
                            className="col-span-2 lg:col-span-5 lg:hidden"
                        >
                            <BlockRenderer blocks={blockAreas.third} />
                        </li>
                    )}

                    {index === 7 && blockAreas?.third?.length > 0 && (
                        <li
                            key="block-area-third"
                            className="col-span-2 hidden lg:col-span-5 lg:block"
                        >
                            <BlockRenderer blocks={blockAreas.third} />
                        </li>
                    )}

                    {productListCreativeItems &&
                        productListCreativeItems.find(
                            x => x.index === index && x.image.src
                        ) && (
                            <li
                                key={`creative-list-item-${index}`}
                                className="col-span-2 supports-[grid-template-rows:_subgrid]:row-[span_2]"
                            >
                                <ProductListCreativeItem
                                    item={productListCreativeItems.find(
                                        x => x.index === index
                                    )}
                                />
                            </li>
                        )}

                    <li
                        key={`${product.id}-${index}`}
                        style={
                            {
                                '--col-start':
                                    index % 14 < 7
                                        ? sixSmallOneBig.colStart(
                                              index,
                                              productsLength
                                          )
                                        : oneBigSixSmall.colStart(
                                              index,
                                              productsLength
                                          ),
                                '--col-end':
                                    index % 14 < 7
                                        ? sixSmallOneBig.colEnd(
                                              index,
                                              productsLength
                                          )
                                        : oneBigSixSmall.colEnd(
                                              index,
                                              productsLength
                                          ),
                            } as React.CSSProperties
                        }
                        className={`flex supports-[grid-template-rows:_subgrid]:row-[span_2] supports-[grid-template-rows:_subgrid]:grid supports-[grid-template-rows:_subgrid]:grid-rows-subgrid lg:col-start-[var(--col-start)] lg:col-end-[var(--col-end)]  ${
                            isBigCard(index)
                                ? 'lg:row-[span_2] supports-[grid-template-rows:_subgrid]:lg:row-[span_4]'
                                : ''
                        }`}
                    >
                        <ProductCard
                            id={product.id}
                            isPurchasable={product.isPurchasable}
                            isOnWishList={product.isOnWishList}
                            enabledWishlist={product.enabledWishlist}
                            images={product.images}
                            name={product.name}
                            url={product.url}
                            videoUrl={product.videoUrl}
                            translations={product.translations}
                            lazy={index > 5}
                            srcSetSizes={getSizeArray(index)}
                            subgrid
                            spanImage={isBigCard(index)}
                            productTag={product.productTag}
                            priceVM={product.priceVM}
                            hasAnyVariantEnabled={product.hasAnyVariantEnabled}
                            productFilters={product.productFilters}
                            showQuantityField={product.showQuantityField}
                            trackingData={product.trackingData}
                            descriptiveVariantName={
                                product.descriptiveVariantName
                            }
                        />
                    </li>
                </>
            ))}
        </ul>
    );
};

export default ProductList5GridBigImage;
